import { useEffect, useMemo, useState } from "react";

/**
 * Lightweight user‑agent / capability hook.
 * SSR safe (returns conservative defaults until mounted).
 */
export interface UserAgentInfo {
  userAgent: string | null;
  platform: string | null;
  browser: { name: string | null; version: string | null };
  os: string | null;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isTouch: boolean;
  isBot: boolean;
  /** True when hydrated on client */
  isReady: boolean;
  /** Re-run detection manually */
  refresh: () => void;
}

const UA_REGEXES = {
  browser: [
    /(edge)\/(\d+\.\d+)/i,
    /(chrome)\/(\d+\.\d+)/i,
    /(firefox)\/(\d+\.\d+)/i,
    /(version)\/(\d+\.\d+).*safari/i, // Safari reports Version/x.y
    /(safari)\/(\d+\.\d+)/i,
    /(brave)\/(\d+\.\d+)/i,
    /(opera)\/(\d+\.\d+)/i,
  ],
  os: [
    /(windows nt 10\.0)/i,
    /(windows nt 11\.0)/i,
    /(mac os x 10[._]\d+)/i,
    /(mac os x 11[._]\d+)/i,
    /(mac os x 12[._]\d+)/i,
    /(android) (\d+)/i,
    /(ios) (\d+)/i,
    /(ipad).*os (\d+)/i,
    /(iphone).*os (\d+)/i,
    /(linux)/i,
  ],
  bot: /(bot|crawler|spider|crawling)/i,
};

function parseBrowser(ua: string): { name: string | null; version: string | null } {
  for (const r of UA_REGEXES.browser) {
    const m = ua.match(r);
    if (m) {
      let name = m[1].toLowerCase();
      if (name === "version") name = "safari";
      return { name, version: m[2] || null };
    }
  }
  return { name: null, version: null };
}

function parseOS(ua: string): string | null {
  for (const r of UA_REGEXES.os) {
    const m = ua.match(r);
    if (m) return m[1].replace(/_/g, ".").toLowerCase();
  }
  return null;
}

function computeCapabilities(ua: string | null): Omit<UserAgentInfo, "refresh"> {
  if (typeof window === "undefined" || !ua) {
    return {
      userAgent: ua,
      platform: null,
      browser: { name: null, version: null },
      os: null,
      isMobile: false,
      isTablet: false,
      isDesktop: false,
      isTouch: false,
      isBot: false,
      isReady: false,
    };
  }

  const nav: any = navigator;
  const platform: string | null = nav.userAgentData?.platform || nav.platform || null;

  // UA Client Hints mobile flag (Chromium based)
  const uaDataMobile = typeof nav.userAgentData?.mobile === "boolean" ? nav.userAgentData.mobile : undefined;

  const lower = ua.toLowerCase();

  const browser = parseBrowser(ua);
  const os = parseOS(ua);

  const isIPad = /ipad/.test(lower) || (platform?.toLowerCase() === "macintel" && navigator.maxTouchPoints > 1);
  const isIPhone = /iphone/.test(lower);
  const android = /android/.test(lower);

  const isTablet = isIPad || (android && !/mobile/.test(lower));
  const isMobile = uaDataMobile !== undefined ? uaDataMobile : /mobile/.test(lower) || isIPhone;
  const isTouch =
    typeof window !== "undefined" &&
    ("ontouchstart" in window || navigator.maxTouchPoints > 0 || navigator.maxTouchPoints > 0);
  const isDesktop = !isMobile && !isTablet;
  const isBot = UA_REGEXES.bot.test(lower);

  return {
    userAgent: ua,
    platform,
    browser,
    os,
    isMobile,
    isTablet,
    isDesktop,
    isTouch,
    isBot,
    isReady: true,
  };
}

export function useUserAgent(): UserAgentInfo {
  const [info, setInfo] = useState<Omit<UserAgentInfo, "refresh">>({
    userAgent: null,
    platform: null,
    browser: { name: null, version: null },
    os: null,
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    isTouch: false,
    isBot: false,
    isReady: false,
  });

  const refresh = () => {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : null;
    setInfo(computeCapabilities(ua));
  };

  useEffect(() => {
    refresh();

    // Listen to changes that might affect classification (resize / orientation / pointer changes)
    const resize = () => refresh();
    window.addEventListener("resize", resize);

    // Some browsers fire pointer change via matchMedia
    const mqs = [
      window.matchMedia("(pointer: coarse)"),
      window.matchMedia("(pointer: fine)"),
      window.matchMedia("(hover: none)"),
      window.matchMedia("(hover: hover)"),
    ];
    const listener = () => refresh();
    mqs.forEach(mq => mq.addEventListener("change", listener));

    return () => {
      window.removeEventListener("resize", resize);
      mqs.forEach(mq => mq.removeEventListener("change", listener));
    };
  }, []);

  return useMemo(() => ({ ...info, refresh }), [info]);
}

export default useUserAgent;
